import type { LoginCredentials, SignupData, UserResponse as User } from 'backend/types'
import type { ReactNode } from 'react'
import { createContext, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  forgotPassword as forgotPasswordApi,
  resetPassword as resetPasswordApi,
} from '@/lib/api/auth'
import { api } from '@/lib/api/client'

interface AuthContextType {
  user: User | null
  isLoading: boolean
  login: (credentials: LoginCredentials) => Promise<void>
  signup: (data: SignupData) => Promise<void>
  logout: () => Promise<void>
  refreshToken: () => Promise<void>
  forgotPassword: (email: string) => Promise<void>
  resetPassword: (token: string, newPassword: string) => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('accessToken')
        if (token) {
          const response = await api.get('/auth/me')
          setUser(response.data.user)
        }
      }
      catch (error) {
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
      }
      finally {
        setIsLoading(false)
      }
    }

    initAuth()
  }, [])

  const login = async (credentials: LoginCredentials) => {
    const response = await api.post('/auth/login', credentials)
    const { user, accessToken, refreshToken } = response.data

    localStorage.setItem('accessToken', accessToken)
    localStorage.setItem('refreshToken', refreshToken)
    setUser(user)

    navigate('/dashboard')
  }

  const signup = async (data: SignupData) => {
    const response = await api.post('/auth/signup', data)
    const { user, accessToken, refreshToken } = response.data

    localStorage.setItem('accessToken', accessToken)
    localStorage.setItem('refreshToken', refreshToken)
    setUser(user)

    navigate('/')
  }

  const logout = async () => {
    try {
      await api.post('/auth/logout')
    }
    catch (error) {
      console.error('Logout error:', error)
    }
    finally {
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      setUser(null)
      navigate('/login')
    }
  }

  const refreshToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken')
      if (!refreshToken)
        throw new Error('No refresh token')

      const response = await api.post('/auth/refresh', { refreshToken })
      const { accessToken, refreshToken: newRefreshToken } = response.data

      localStorage.setItem('accessToken', accessToken)
      localStorage.setItem('refreshToken', newRefreshToken)
    }
    catch (error) {
      await logout()
      throw error
    }
  }

  const forgotPassword = async (email: string) => {
    const result = await forgotPasswordApi(email)
    if (!result.success) {
      throw new Error(result.message || 'Failed to send password reset email')
    }
  }

  const resetPassword = async (token: string, newPassword: string) => {
    const result = await resetPasswordApi(token, newPassword)
    if (!result.success) {
      throw new Error(result.message || 'Failed to reset password')
    }
  }

  const refreshUser = async () => {
    try {
      const response = await api.get('/auth/me')
      setUser(response.data.user)
    }
    catch (e) {
      console.error('Failed to refresh user', e)
      // optionally: await logout()
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        login,
        signup,
        logout,
        refreshToken,
        forgotPassword,
        resetPassword,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
